import { Avatar } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Icon } from "../../components/Icon";
import { IconButton } from "../../components/IconButton";
import { Tooltip } from "../../components/Tooltip";
import { useCopyToClipboard } from "../../hooks";
import { ArrowDown, ArrowUp, Clipboard, ClipboardCheck, Minus } from "../../icons/v2-stroke";
import { cn } from "../../lib/utils";
import React from "react";
import { DENSITY_ROW_HEIGHT_CLASS, SCROLL_COLUMN_MIN_WIDTH_CLASS, useDataTableLayout } from "./layout";
import { ALIGN_JUSTIFY_CLASS, ALIGN_TEXT_CLASS, getDataTableColumnPresets } from "./presets";
/** Body cell (td, or th scope="row" with meta.rowHeader) with truncation, density height and column meta styling. */
export function Cell({
  children,
  className,
  column,
  ...props
}) {
  var _column$columnDef$met, _column$columnDef$met2;
  const layout = useDataTableLayout();
  const presets = getDataTableColumnPresets(column);
  const isRowHeader = ((_column$columnDef$met = column.columnDef.meta) === null || _column$columnDef$met === void 0 ? void 0 : _column$columnDef$met.rowHeader) === true;
  const Tag = isRowHeader ? "th" : "td";
  return /*#__PURE__*/React.createElement(Tag, {
    scope: isRowHeader ? "row" : undefined,
    className: cn(DENSITY_ROW_HEIGHT_CLASS[layout.density], "truncate px-2", isRowHeader && "text-left font-normal", layout.enforceColumnMinWidth && presets.sortable && SCROLL_COLUMN_MIN_WIDTH_CLASS, presets.cellClassName, presets.align !== "left" && ALIGN_TEXT_CLASS[presets.align], (_column$columnDef$met2 = column.columnDef.meta) === null || _column$columnDef$met2 === void 0 ? void 0 : _column$columnDef$met2.className, className),
    ...props
  }, presets.align === "left" ? children :
  /*#__PURE__*/
  // Cell helpers are flex rows, which ignore text-align; justify them instead.
  React.createElement("div", {
    className: cn("flex items-center", ALIGN_JUSTIFY_CLASS[presets.align])
  }, children));
}
function CellLeadingVisual({
  avatarUrl,
  avatarName,
  roundedAvatar,
  avatarStack,
  icon,
  iconClassName,
  avatarSize
}) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, (avatarUrl || avatarName) && /*#__PURE__*/React.createElement(Avatar, {
    visual: avatarUrl,
    name: avatarName,
    size: avatarSize,
    className: "mr-2",
    isRounded: roundedAvatar !== null && roundedAvatar !== void 0 ? roundedAvatar : false
  }), avatarStack && /*#__PURE__*/React.createElement(Avatar.Stack, {
    ...avatarStack,
    avatars: avatarStack.items,
    size: avatarSize
  }), icon && /*#__PURE__*/React.createElement(Icon, {
    visual: icon,
    size: "sm",
    className: cn("mr-2 text-foreground", iconClassName)
  }));
}
/**
 * @cc [owner:aubin-tchoi,label:react] inline-control-alignment
 * Direct button children MUST align with the middle of the primary text line.
 */
function CellText({
  children,
  description,
  grow,
  secondaryLine,
  showSecondaryLine
}) {
  const primaryLine = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: cn(grow ? "flex-grow" : "",
    // Inline controls should align with the text line, not its baseline.
    "truncate text-sm [&>button]:align-middle", "text-foreground")
  }, children), description && /*#__PURE__*/React.createElement("span", {
    className: cn("pl-2 text-sm", "text-muted-foreground")
  }, description));
  if (showSecondaryLine) {
    return /*#__PURE__*/React.createElement("div", {
      className: cn("flex min-w-0 shrink flex-col truncate", grow ? "flex-grow" : "")
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center truncate"
    }, primaryLine), /*#__PURE__*/React.createElement("div", {
      className: "truncate text-xs text-muted-foreground"
    }, secondaryLine));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex shrink truncate items-center", grow ? "flex-grow" : "")
  }, primaryLine);
}

/** Standard cell layout with optional avatar, avatar stack, icon, inline description, secondary line and trailing slot. */
export function CellContent({
  children,
  className,
  avatarUrl,
  avatarName,
  roundedAvatar,
  icon,
  iconClassName,
  description,
  grow = false,
  disabled,
  avatarStack,
  secondaryLine,
  trailing,
  ...props
}) {
  const {
    density
  } = useDataTableLayout();
  const avatarSize = density === "relaxed" ? "sm" : "xs";
  const showSecondaryLine = secondaryLine !== undefined && density === "relaxed";
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex items-center", grow ? "flex-grow" : "", disabled && "cursor-not-allowed opacity-50", className),
    "aria-disabled": disabled || undefined,
    ...props
  }, /*#__PURE__*/React.createElement(CellLeadingVisual, {
    avatarUrl: avatarUrl,
    avatarName: avatarName,
    roundedAvatar: roundedAvatar,
    avatarStack: avatarStack,
    icon: icon,
    iconClassName: iconClassName,
    avatarSize: avatarSize
  }), /*#__PURE__*/React.createElement(CellText, {
    description: description,
    grow: grow,
    secondaryLine: secondaryLine,
    showSecondaryLine: showSecondaryLine
  }, children), trailing !== undefined && /*#__PURE__*/React.createElement("div", {
    className: "ml-auto flex shrink-0 items-center pl-2"
  }, trailing));
}
/** Simple muted text cell with an optional tooltip and hover copy-to-clipboard button. */
export function BasicCellContent({
  label,
  tooltip,
  className,
  textToCopy,
  disabled,
  ...props
}) {
  const [isCopied, copyToClipboard] = useCopyToClipboard();
  const {
    density
  } = useDataTableLayout();
  const cellHeight = DENSITY_ROW_HEIGHT_CLASS[density];
  const handleCopy = async () => {
    const textToUse = textToCopy !== null && textToCopy !== void 0 ? textToCopy : String(label);
    void copyToClipboard(new ClipboardItem({
      "text/plain": new Blob([String(textToUse)], {
        type: "text/plain"
      })
    }));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    tooltipTriggerAsChild: true,
    trigger: /*#__PURE__*/React.createElement("div", {
      className: cn(cellHeight, "group flex items-center gap-2 text-sm", "text-muted-foreground", disabled && "cursor-not-allowed opacity-50", className),
      "aria-disabled": disabled || undefined,
      ...props
    }, /*#__PURE__*/React.createElement("span", {
      className: "truncate"
    }, label), textToCopy && /*#__PURE__*/React.createElement(Button, {
      icon: isCopied ? ClipboardCheck : Clipboard,
      className: "hidden group-hover:block",
      variant: "outline",
      onClick: async e => {
        e.stopPropagation();
        await handleCopy();
      },
      size: "xs"
    })),
    label: tooltip
  }) : /*#__PURE__*/React.createElement("div", {
    className: cn(cellHeight, "group flex items-center gap-2 text-sm", "text-muted-foreground", disabled && "cursor-not-allowed opacity-50", className),
    "aria-disabled": disabled || undefined,
    ...props
  }, /*#__PURE__*/React.createElement("span", {
    className: "truncate"
  }, label), textToCopy && /*#__PURE__*/React.createElement(Button, {
    icon: isCopied ? ClipboardCheck : Clipboard,
    className: "hidden group-hover:block",
    variant: "outline",
    onClick: async e => {
      e.stopPropagation();
      await handleCopy();
    },
    size: "xs"
  })));
}
function formatNumericValue(value, locale, precision) {
  return value.toLocaleString(locale, precision === undefined ? undefined : {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  });
}
const TREND_LABEL = {
  up: "Trending up",
  down: "Trending down",
  flat: "No change"
};

/** Display string for a numeric cell: placeholder for missing values, otherwise the localized number with its unit. */
function formatNumericCellValue({
  value,
  locale,
  precision,
  unit,
  unitPosition,
  placeholder
}) {
  if (value === null || value === undefined) {
    return placeholder;
  }
  const formatted = formatNumericValue(value, locale, precision);
  if (unit === undefined) {
    return formatted;
  }
  return unitPosition === "prefix" ? `${unit}${formatted}` : `${formatted}${unit}`;
}
function TrendIndicator({
  trend,
  upIsPositive
}) {
  const trendIsPositive = trend === "up" ? upIsPositive : trend === "down" ? !upIsPositive : null;
  return /*#__PURE__*/React.createElement("span", {
    role: "img",
    "aria-label": TREND_LABEL[trend],
    className: cn("flex items-center", trendIsPositive === true && "text-success-800", trendIsPositive === false && "text-warning-800", trendIsPositive === null && "text-muted-foreground")
  }, /*#__PURE__*/React.createElement(Icon, {
    visual: trend === "up" ? ArrowUp : trend === "down" ? ArrowDown : Minus,
    size: "xs"
  }));
}

/** Right-aligned number in tabular figures with optional unit and trend arrow; pair with a `meta.type: "numeric"` column so the header aligns too. */
export function NumericCellContent({
  value,
  locale,
  precision,
  unit,
  unitPosition = "suffix",
  placeholder = "-",
  trend,
  upIsPositive = true,
  tooltip,
  disabled,
  className,
  ...props
}) {
  const {
    density
  } = useDataTableLayout();
  const formatted = formatNumericCellValue({
    value,
    locale,
    precision,
    unit,
    unitPosition,
    placeholder
  });
  const content = /*#__PURE__*/React.createElement("div", {
    className: cn(DENSITY_ROW_HEIGHT_CLASS[density], "flex items-center justify-end gap-1 text-sm tabular-nums whitespace-nowrap", "text-foreground", disabled && "cursor-not-allowed opacity-50", className),
    "aria-disabled": disabled || undefined,
    ...props
  }, /*#__PURE__*/React.createElement("span", {
    className: "truncate"
  }, formatted), trend && /*#__PURE__*/React.createElement(TrendIndicator, {
    trend: trend,
    upIsPositive: upIsPositive
  }));
  return tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    tooltipTriggerAsChild: true,
    trigger: content,
    label: tooltip
  }) : content;
}
/** Status as a mini Chip; pair with a `meta.type: "status"` column to keep the label on one line. */
export function StatusCellContent({
  label,
  color = "primary",
  icon,
  isBusy,
  tooltip,
  className
}) {
  const {
    density
  } = useDataTableLayout();
  const content = /*#__PURE__*/React.createElement("div", {
    className: cn(DENSITY_ROW_HEIGHT_CLASS[density], "flex items-center", className)
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "mini",
    color: color,
    label: label,
    icon: icon,
    isBusy: isBusy
  }));
  return tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    tooltipTriggerAsChild: true,
    trigger: content,
    label: tooltip
  }) : content;
}
/** Cell content with a persistent copy-to-clipboard icon button. */
export function CellContentWithCopy({
  children,
  textToCopy,
  className
}) {
  const [isCopied, copyToClipboard] = useCopyToClipboard();
  const handleCopy = async () => {
    void copyToClipboard(new ClipboardItem({
      "text/plain": new Blob([textToCopy !== null && textToCopy !== void 0 ? textToCopy : String(children)], {
        type: "text/plain"
      })
    }));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex items-center space-x-2", className)
  }, /*#__PURE__*/React.createElement("span", {
    className: "truncate"
  }, children), /*#__PURE__*/React.createElement(IconButton, {
    icon: isCopied ? ClipboardCheck : Clipboard,
    variant: "outline",
    onClick: async e => {
      e.stopPropagation();
      await handleCopy();
    },
    size: "xs"
  }));
}

/** Table caption element. */
export function Caption({
  children,
  className,
  ...props
}) {
  return /*#__PURE__*/React.createElement("caption", {
    className: className,
    ...props
  }, children);
}
//# sourceMappingURL=cells.js.map